// Labs Manager Component - Manage college labs
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Button } from '../ui/button';
import ImageUpload from './ImageUpload';
import { getImageUrl } from '../../lib/utils';

const LabsManager = ({ collegeId }) => {
  const [labs, setLabs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name_ar: '',
    description_ar: '',
    image: '',
    college_id: collegeId
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (collegeId) {
      fetchLabs();
    }
  }, [collegeId]);

  const fetchLabs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/labs/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      // Filter labs by college
      setLabs(data.filter(lab => lab.college_id === collegeId));
    } catch (error) {
      console.error('Error fetching labs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const labData = { ...formData, college_id: collegeId };
    
    const url = editing
      ? `${BACKEND_URL}/api/labs/${editing}`
      : `${BACKEND_URL}/api/labs/`;
    const method = editing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(labData),
      });
      if (response.ok) {
        fetchLabs();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving lab:', error);
    }
  };

  const handleEdit = (lab) => {
    setEditing(lab.id);
    setFormData({
      name_ar: lab.name_ar,
      description_ar: lab.description_ar,
      image: lab.image || '',
      college_id: collegeId
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المعمل؟')) return;
    try {
      await fetch(`${BACKEND_URL}/api/labs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      fetchLabs();
    } catch (error) {
      console.error('Error deleting lab:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name_ar: '',
      description_ar: '',
      image: '',
      college_id: collegeId
    });
    setEditing(null);
    setShowForm(false);
  };

  return (
    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-lg border border-teal-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
          <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">🔬</span>
          معامل الكلية
        </h3>
        {!showForm && (
          <Button
            type="button"
            size="sm"
            onClick={() => setShowForm(true)}
            className="bg-[#0F388E]"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة معمل
          </Button>
        )}
      </div>

      {/* Labs List */}
      {!showForm && labs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {labs.map(lab => (
            <div key={lab.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
              <div className="flex gap-3">
                {lab.image && (
                  <img 
                    src={getImageUrl(lab.image)} 
                    alt={lab.name_ar} 
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h4 className="font-bold text-[#0F388E] mb-1">{lab.name_ar}</h4>
                  <p className="text-sm text-gray-600 line-clamp-2">{lab.description_ar}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(lab)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(lab.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!showForm && labs.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-4">
          لا توجد معامل مضافة. اضغط "إضافة معمل" لإضافة أول معمل للكلية.
        </p>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg p-6 space-y-4">
          <h4 className="font-bold text-[#0F388E] mb-4">
            {editing ? 'تعديل المعمل' : 'إضافة معمل جديد'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اسم المعمل *
              </label>
              <input
                type="text"
                value={formData.name_ar}
                onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                placeholder="مثال: معمل التشريح"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                وصف المعمل *
              </label>
              <textarea
                value={formData.description_ar}
                onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
                placeholder="اكتب وصفاً عن المعمل ومحتوياته..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                required
              />
            </div>

            <ImageUpload
              label="صورة المعمل"
              value={formData.image}
              onChange={(v) => setFormData({...formData, image: v})}
            />

            <div className="flex gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
              >
                إلغاء
              </Button>
              <Button type="submit" className="bg-[#0F388E]">
                {editing ? 'تحديث المعمل' : 'إضافة المعمل'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default LabsManager;
